import { useAnalyticsLogEventMutation } from "@shared/data/mutations";
import type { Platforms } from "@shared/types/platforms";

import type { StoreEntryPoint, AnalyticsEvent, PatientEntryPoint } from ".";

type AnalyticsPayload = {
  onboarding_step?: string;
  practitioner_id?: string;
  default_variant?: string;
  product_status?: string;
  entry_point?: StoreEntryPoint | PatientEntryPoint;
  patient_id?: string;
  order_id?: string;
  shipping_address_id?: string;
  number_of_items?: number;
  number_of_products?: number;
  treatment_plan_id?: string;
  treatment_plan_template_id?: string;
  content_type?: string;
  content_name?: string;
  update_type?: string;
  update_value?: string;
  sku?: string;
  sku_list?: string[];
  discount?: number;
  current_onboarding_step?: string;
  account_margin_type?: string;
  account_modality?: string;
  margin?: number;
  previous_margin?: number;
  error_message?: string;
  error_type?: "validation" | "network";
  attribute?: "view_integration" | "dismiss";
  url?: string;
  shareable_type?: string;
  lab_id?: string;
  lab_count?: number;
  lab_test_ids?: number[];
  has_message?: boolean;
  catalog_permission?: "open" | "restricted" | "private_categories" | "closed";
  modification_type?: string;
  modification_value?: string;
  fields_changed?: string[];
  page_version?: string;
  cart_count?: number;
  search_rank?: number;
  brand?: string;
  contains_store_logo?: boolean;
  form_version?: string;
  signin_method?: string;
  has_treatment_plan?: boolean;
  utm_campaign?: string;
  utm_medium?: string;
  utm_source?: string;
  search_version?: number;
  event_message?: string;
  status_of_products?: string[];
  page_url?: string;
  platform_select?: Platforms.FULLSCRIPT | Platforms.WELLEVATE;
};

enum SearchType {
  PRODUCT = "Product",
  INGREDIENT = "Ingredient",
  BRAND = "Brand",
  SUPPLEMENT_TYPE = "SupplementType",
  CONDITION = "Condition",
  QUERY = "query",
}

enum FilterType {
  BRAND = "brand",
  SUPPLEMENT_TYPE = "supplement_type",
  CONDITION = "condition",
  INCLUDE_INGREDIENT = "include_ingredient",
  EXCLUDE_INGREDIENT = "exclude_ingredient",
  AVAILABILITY = "availability",
  FAVORITES = "favorites",
  DEMOGRAPHIC = "demographic",
  FORM = "form",
  ALLERGENS = "allergens",
  CERTIFICATIONS = "certifications",
}

interface AnalyticsSearchPayload extends Pick<AnalyticsPayload, "entry_point"> {
  content: string;
  type: SearchType;
}

interface AnalyticsFilterPayload extends Pick<AnalyticsPayload, "entry_point"> {
  content: string;
  filter_type: FilterType;
}

interface Props {
  eventName: AnalyticsEvent;
  payload: AnalyticsPayload | AnalyticsSearchPayload | AnalyticsFilterPayload;
}

const useAnalyticsLogger = () => {
  const [logAnalyticsStep] = useAnalyticsLogEventMutation();
  const logAnalyticsEvent = (input: Props) => {
    logAnalyticsStep({
      variables: {
        input,
      },
    });
  };
  return logAnalyticsEvent;
};

export { useAnalyticsLogger, SearchType, FilterType };
